import React, { useState } from "react";

export type InputMode = "casual" | "structured";

export interface DesignInputProps {
  onSubmit: (description: string) => void;
  disabled?: boolean;
}

const CASUAL_PLACEHOLDER =
  "e.g. A simple LED blinker circuit using a 555 timer, two 10kΩ resistors, a 100nF capacitor, and 3 red LEDs powered by a 9V battery.";

const STRUCTURED_PLACEHOLDER = `e.g.
1. Input connector J1
Symbol: Connector_Generic:Conn_01x02
RefDes: J1
J1.1 → VIN
J1.2 → GND

2. Resistor R1
Symbol: Device:R
RefDes: R1
Suggested value: 10kΩ
R1.1 → VIN
R1.2 → LED_ANODE

3. LED D1
Symbol: Device:LED
RefDes: D1
D1.A → LED_ANODE
D1.K → GND`;

const CASUAL_EXAMPLE = `Design a 555 timer astable multivibrator circuit that blinks an LED at approximately 1 Hz.

Components needed:
- 555 timer IC
- Two resistors (10kΩ and 47kΩ) for timing
- One 10uF electrolytic capacitor for timing
- One 100nF bypass capacitor on pin 5
- One red LED with a 330Ω current-limiting resistor
- A 2-pin connector for 9V battery input
- A push button for on/off control

The circuit should use a standard astable configuration with the output on pin 3 driving the LED through the current-limiting resistor to GND.`;

const STRUCTURED_EXAMPLE = `Pin-by-pin wiring checklist for LED Driver Board

1. Input connector J1
Symbol
Use: Connector_Generic:Conn_01x02
RefDes
J1
J1.1 → VIN_5V
J1.2 → GND

2. Bypass capacitor C1
Symbol
Use: Device:C
RefDes
C1
Suggested value: 100nF
C1.1 → VIN_5V
C1.2 → GND

3. Current-limiting resistor R1
Symbol
Use: Device:R
RefDes
R1
Suggested value: 330Ω
R1.1 → VIN_5V
R1.2 → LED_ANODE

4. LED D1
Symbol
Use: Device:LED
RefDes
D1
D1.1 → LED_ANODE
D1.2 → GND

5. Test point TP1
RefDes
TP1
TP1.1 → VIN_5V

Clean net map:
J1.1 → VIN_5V
VIN_5V → C1.1 → GND
VIN_5V → R1.1
R1.2 → LED_ANODE
LED_ANODE → D1.1
D1.2 → GND`;

const DesignInput: React.FC<DesignInputProps> = ({ onSubmit, disabled = false }) => {
  const [description, setDescription] = useState("");
  const [mode, setMode] = useState<InputMode>("casual");
  const [showExample, setShowExample] = useState(false);
  const [fileName, setFileName] = useState<string | null>(null);
  const fileInputRef = React.useRef<HTMLInputElement>(null);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const trimmed = description.trim();
    if (trimmed.length === 0) return;
    onSubmit(trimmed);
  };

  const handleLoadExample = () => {
    setDescription(mode === "casual" ? CASUAL_EXAMPLE : STRUCTURED_EXAMPLE);
    setFileName(null);
    setShowExample(false);
  };

  const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => {
      const text = ev.target?.result;
      if (typeof text === "string") {
        setDescription(text);
        setFileName(file.name);
        // Auto-detect mode: if text has RefDes + arrow wiring, switch to structured
        if (/RefDes/i.test(text) && /[A-Z]{1,3}\d+\.[A-Za-z0-9_]+\s*(?:\u2192|->)/.test(text)) {
          setMode("structured");
        }
      }
    };
    reader.readAsText(file);
    // Reset so the same file can be re-selected
    e.target.value = "";
  };

  return (
    <form className="design-input" onSubmit={handleSubmit}>
      <label htmlFor="hw-description">Describe your hardware design</label>

      <fieldset className="mode-selector">
        <legend>Input mode</legend>
        <label className={`mode-option ${mode === "casual" ? "mode-active" : ""}`}>
          <input
            type="radio"
            name="inputMode"
            value="casual"
            checked={mode === "casual"}
            onChange={() => setMode("casual")}
            disabled={disabled}
          />
          <span className="mode-label">
            <strong>Casual</strong>
            <small>Describe your circuit in plain English</small>
          </span>
        </label>
        <label className={`mode-option ${mode === "structured" ? "mode-active" : ""}`}>
          <input
            type="radio"
            name="inputMode"
            value="structured"
            checked={mode === "structured"}
            onChange={() => setMode("structured")}
            disabled={disabled}
          />
          <span className="mode-label">
            <strong>Structured</strong>
            <small>Pin-by-pin wiring spec with RefDes &amp; symbols</small>
          </span>
        </label>
      </fieldset>

      {mode === "casual" && (
        <div className="mode-hint">
          💡 List components naturally — e.g. <em>"two 10kΩ resistors, a 555 timer, and 3 LEDs"</em>.
          The system will pick KiCad symbols and generate the netlist automatically.
        </div>
      )}
      {mode === "structured" && (
        <div className="mode-hint">
          📐 Provide explicit <code>RefDes</code>, <code>Symbol</code> (e.g. <code>Device:R</code>),
          and wiring like <code>R1.1 → VIN</code>. The system will use your exact component
          definitions and net connections.
        </div>
      )}

      <textarea
        id="hw-description"
        value={description}
        onChange={(e) => { setDescription(e.target.value); setFileName(null); }}
        placeholder={mode === "casual" ? CASUAL_PLACEHOLDER : STRUCTURED_PLACEHOLDER}
        rows={mode === "structured" ? 14 : 6}
        disabled={disabled}
        aria-label="Hardware description"
      />

      {fileName && (
        <div className="file-badge">
          📄 Loaded: <strong>{fileName}</strong>
          <button
            type="button"
            className="btn-clear"
            onClick={() => { setDescription(""); setFileName(null); }}
            disabled={disabled}
            aria-label="Clear loaded file"
          >
            ✕
          </button>
        </div>
      )}

      <input
        ref={fileInputRef}
        type="file"
        accept=".txt,.md,.text,.json"
        onChange={handleFileUpload}
        style={{ display: "none" }}
        disabled={disabled}
      />

      <div className="input-actions">
        <button type="submit" disabled={disabled || description.trim().length === 0}>
          Generate Design
        </button>
        <button
          type="button"
          className="btn-secondary"
          onClick={() => fileInputRef.current?.click()}
          disabled={disabled}
        >
          📁 Upload File
        </button>
        <button
          type="button"
          className="btn-secondary"
          onClick={() => setShowExample(!showExample)}
          disabled={disabled}
        >
          {showExample ? "Hide Example" : "Show Example"}
        </button>
        {showExample && (
          <button
            type="button"
            className="btn-secondary"
            onClick={handleLoadExample}
            disabled={disabled}
          >
            Load Example
          </button>
        )}
      </div>

      {showExample && (
        <details className="example-panel" open>
          <summary>
            {mode === "casual" ? "Casual description example" : "Structured wiring spec example"}
          </summary>
          <pre className="example-code">
            {mode === "casual" ? CASUAL_EXAMPLE : STRUCTURED_EXAMPLE}
          </pre>
        </details>
      )}
    </form>
  );
};

export default DesignInput;
